import * as z from "zod";

import {
  CreateFeeConfigInput,
  FeeBreakdown,
  FeeConfig,
  FeeConfigStatusEnum,
  FeeRule,
  FeeRuleBreakdown,
  FeeTier,
  GroupedFeeConfig,
} from "./fee-config.type";
import { toMajorUnit, toMinorUnit } from "./pricing-configurator.const";

const TWO_DECIMALS_MESSAGE = "Maximum 2 décimales";

export const hasMaxTwoDecimals = (value: number): boolean => Math.round(value * 100) / 100 === value;

const requiredNumber = (requiredMessage: string) =>
  z.number({ error: (issue) => (issue.input === undefined ? requiredMessage : "Valeur invalide") });

const sharePercentSchema = requiredNumber("La part est requise")
  .min(0, "La part doit être comprise entre 0 et 100")
  .max(100, "La part doit être comprise entre 0 et 100")
  .refine(hasMaxTwoDecimals, TWO_DECIMALS_MESSAGE);

const feePercentSchema = requiredNumber("Le pourcentage est requis")
  .min(0, "Le pourcentage doit être compris entre 0 et 100")
  .max(100, "Le pourcentage doit être compris entre 0 et 100")
  .refine(hasMaxTwoDecimals, TWO_DECIMALS_MESSAGE);

const feeAmountSchema = z
  .number()
  .min(0, "Le montant doit être supérieur ou égal à 0")
  .refine(hasMaxTwoDecimals, TWO_DECIMALS_MESSAGE);

const tierClampFields = {
  feePercent: feePercentSchema,
  minFee: feeAmountSchema.optional(),
  maxFee: feeAmountSchema.optional(),
};

const requireMinBelowMax = (
  tier: { minFee?: number; maxFee?: number },
  ctx: z.core.$RefinementCtx<{ minFee?: number; maxFee?: number }>,
) => {
  if (tier.minFee !== undefined && tier.maxFee !== undefined && tier.minFee > tier.maxFee) {
    ctx.addIssue({
      code: "custom",
      message: "Le minimum doit être inférieur ou égal au maximum",
      path: ["maxFee"],
    });
  }
};

const boundedTierSchema = z
  .object({
    upTo: requiredNumber("Le prix de fin de tranche est requis")
      .positive("Le prix de fin de tranche doit être supérieur à 0")
      .refine(hasMaxTwoDecimals, TWO_DECIMALS_MESSAGE),
    ...tierClampFields,
  })
  .superRefine(requireMinBelowMax);

const catchAllTierSchema = z.object(tierClampFields).superRefine(requireMinBelowMax);

const feeRuleFormSchema = z
  .object({
    tiers: z.array(boundedTierSchema),
    catchAllEnabled: z.boolean(),
    catchAll: catchAllTierSchema.optional(),
    overrideShares: z.boolean(),
    customerShare: sharePercentSchema.optional(),
  })
  .superRefine((rule, ctx) => {
    if (rule.tiers.length === 0 && !rule.catchAllEnabled) {
      ctx.addIssue({ code: "custom", message: "Ajoutez au moins une tranche", path: ["tiers"] });
    }
    if (rule.catchAllEnabled && !rule.catchAll) {
      ctx.addIssue({
        code: "custom",
        message: "Renseignez la commission au-delà de la dernière tranche",
        path: ["catchAll"],
      });
    }
    if (rule.overrideShares && rule.customerShare === undefined) {
      ctx.addIssue({ code: "custom", message: "La part client est requise", path: ["customerShare"] });
    }
    for (let i = 1; i < rule.tiers.length; i++) {
      const previous = rule.tiers[i - 1]?.upTo;
      const current = rule.tiers[i]?.upTo;
      if (previous !== undefined && current !== undefined && current <= previous) {
        ctx.addIssue({
          code: "custom",
          message: "Chaque tranche doit se terminer à un prix plus élevé que la précédente",
          path: ["tiers", i, "upTo"],
        });
      }
    }
  });

export const feeConfigFormSchema = z.object({
  name: z.string().trim().min(1, "Le nom est requis"),
  customerShare: sharePercentSchema,
  rules: z.array(feeRuleFormSchema).min(1, "Au moins une règle est requise"),
});

export type FeeTierFormValues = z.infer<typeof boundedTierSchema>;
export type FeeCatchAllFormValues = z.infer<typeof catchAllTierSchema>;
export type FeeRuleFormValues = z.infer<typeof feeRuleFormSchema>;
export type FeeConfigFormValues = z.infer<typeof feeConfigFormSchema>;

export const createEmptyTier = (): FeeTierFormValues => ({ upTo: 0, feePercent: 0 });

export const createEmptyCatchAll = (): FeeCatchAllFormValues => ({ feePercent: 0 });

export const createEmptyRule = (): FeeRuleFormValues => ({
  tiers: [createEmptyTier()],
  catchAllEnabled: false,
  overrideShares: false,
});

/** Initial form state for the "new fee" editor only — nothing is persisted until the form is submitted. */
export const NEW_FEE_FORM_PREFILL: FeeConfigFormValues = {
  name: "",
  customerShare: 100,
  rules: [
    {
      tiers: [{ upTo: 20, feePercent: 10, minFee: 0.1, maxFee: 0.5 }],
      catchAllEnabled: true,
      catchAll: { feePercent: 2.5 },
      overrideShares: false,
    },
  ],
};

export const complementShare = (share: number): number => Math.round((100 - share) * 100) / 100;

const toApiClamps = (tier: FeeCatchAllFormValues) => ({
  feePercent: tier.feePercent,
  ...(tier.minFee !== undefined && { minFee: toMinorUnit(tier.minFee) }),
  ...(tier.maxFee !== undefined && { maxFee: toMinorUnit(tier.maxFee) }),
});

export const feeConfigFormToInput = (values: FeeConfigFormValues): CreateFeeConfigInput => ({
  name: values.name.trim(),
  customerShare: values.customerShare,
  cityShare: complementShare(values.customerShare),
  rules: values.rules.map((rule) => ({
    tiers: [
      ...rule.tiers.map((tier) => ({ upTo: toMinorUnit(tier.upTo), ...toApiClamps(tier) })),
      ...(rule.catchAllEnabled && rule.catchAll ? [toApiClamps(rule.catchAll)] : []),
    ],
    ...(rule.overrideShares && rule.customerShare !== undefined
      ? { customerShare: rule.customerShare, cityShare: complementShare(rule.customerShare) }
      : {}),
  })),
});

const toFormClamps = (tier: FeeTier): FeeCatchAllFormValues => ({
  feePercent: tier.feePercent,
  ...(tier.minFee !== undefined && { minFee: toMajorUnit(tier.minFee) }),
  ...(tier.maxFee !== undefined && { maxFee: toMajorUnit(tier.maxFee) }),
});

export const feeConfigToFormValues = (config: FeeConfig): FeeConfigFormValues => ({
  name: config.name,
  customerShare: config.customerShare,
  rules: config.rules.map((rule) => {
    const catchAll = rule.tiers.find((tier) => tier.upTo === undefined);
    const bounded = rule.tiers.filter((tier): tier is FeeTier & { upTo: number } => tier.upTo !== undefined);
    return {
      tiers: bounded.map((tier) => ({ upTo: toMajorUnit(tier.upTo), ...toFormClamps(tier) })),
      catchAllEnabled: catchAll !== undefined,
      ...(catchAll !== undefined && { catchAll: toFormClamps(catchAll) }),
      overrideShares: rule.customerShare !== undefined,
      ...(rule.customerShare !== undefined && { customerShare: rule.customerShare }),
    };
  }),
});

const evaluateRule = (amount: number, rule: FeeRule): number => {
  for (const tier of rule.tiers) {
    if (tier.upTo === undefined || amount < tier.upTo) {
      let output = Math.floor(amount * (tier.feePercent / 100));
      if (tier.minFee !== undefined && output < tier.minFee) output = tier.minFee;
      if (tier.maxFee !== undefined && output > tier.maxFee) output = tier.maxFee;
      return output;
    }
  }
  return 0;
};

/** Mirrors clients-api src/pricing/fees/fee-evaluator.ts (minor units, floor at every stage) — keep in sync. */
export const evaluateFees = (amount: number, config: Pick<FeeConfig, "rules" | "customerShare">): FeeBreakdown => {
  if (amount <= 0) {
    return { total: 0, customerShare: 0, citySettlement: 0, perRule: [] };
  }

  const perRule: FeeRuleBreakdown[] = [];
  let total = 0;
  let customerShare = 0;

  config.rules.forEach((rule, ruleIndex) => {
    const output = evaluateRule(amount, rule);
    const sharePercent = rule.customerShare ?? config.customerShare;
    const ruleCustomerShare = Math.floor(output * (sharePercent / 100));
    perRule.push({ ruleIndex, output, customerShare: ruleCustomerShare });
    total += output;
    customerShare += ruleCustomerShare;
  });

  return { total, customerShare, citySettlement: total - customerShare, perRule };
};

const feeStatusPriority = (status: FeeConfigStatusEnum) => {
  if (status === FeeConfigStatusEnum.DRAFT) return 0;
  if (status === FeeConfigStatusEnum.ENABLED) return 1;
  return 2;
};

export const groupFeeConfigsByLogicalFeeId = (configs: FeeConfig[]): GroupedFeeConfig[] => {
  const groups = new Map<string, FeeConfig[]>();

  for (const config of configs) {
    if (!groups.has(config.logicalFeeId)) groups.set(config.logicalFeeId, []);
    groups.get(config.logicalFeeId)?.push(config);
  }

  return Array.from(groups.entries()).map(([logicalFeeId, versions]) => {
    versions.sort((a, b) => (b.version ?? 0) - (a.version ?? 0));

    const current = [...versions].sort((a, b) => feeStatusPriority(a.status) - feeStatusPriority(b.status))[0];

    const currentVersions = versions.filter(
      (v) => v.status === FeeConfigStatusEnum.DRAFT || v.status === FeeConfigStatusEnum.ENABLED,
    );
    const previousVersions = versions.filter((v) => v.status === FeeConfigStatusEnum.DISABLED);

    return { logicalFeeId, current, allVersions: versions, currentVersions, previousVersions };
  });
};

export const getFeeConfigVersionLabel = (config: FeeConfig): string => {
  const versionNum = config.version != null ? `v${config.version}` : "brouillon";
  const date = config.publishedAt ? ` - ${new Date(config.publishedAt).toLocaleDateString("fr-FR")}` : "";
  return `${versionNum}${date}`;
};

export const formatFrNumber = (value: number): string => value.toLocaleString("fr-FR");

export const formatFeeAmount = (minorAmount: number): string =>
  `${toMajorUnit(minorAmount).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export const describeRuleTiers = (rule: FeeRule): string[] => {
  const lines: string[] = [];
  let previousUpTo: number | undefined;
  for (const tier of rule.tiers) {
    lines.push(describeTier(tier, previousUpTo));
    previousUpTo = tier.upTo;
  }
  return lines;
};

export const describeTier = (tier: FeeTier, previousUpTo?: number): string => {
  const percent = `${formatFrNumber(tier.feePercent)} %`;
  const clamps = [
    tier.minFee !== undefined ? `min ${formatFeeAmount(tier.minFee)}` : null,
    tier.maxFee !== undefined ? `max ${formatFeeAmount(tier.maxFee)}` : null,
  ].filter(Boolean);
  const clampsLabel = clamps.length > 0 ? ` (${clamps.join(", ")})` : "";
  const range =
    tier.upTo !== undefined
      ? previousUpTo !== undefined
        ? `De ${formatFeeAmount(previousUpTo)} à ${formatFeeAmount(tier.upTo)}`
        : `Jusqu'à ${formatFeeAmount(tier.upTo)}`
      : previousUpTo !== undefined
        ? `Au-delà de ${formatFeeAmount(previousUpTo)}`
        : "Sur tout le prix";
  return `${range} : ${percent}${clampsLabel}`;
};
